import type { SequenceDocument, SequenceEvent, Track } from '../format/types';
import type { ChordPayload, DrumPayload, NotePayload, ReleaseAction, RuntimeAction, TriggerAction } from './actions';
import { IntervalTimerDriver, type Clock, type TimerDriver } from './clock';

const DEFAULT_LOOKAHEAD_SECONDS = 0.1;
const DEFAULT_TICK_INTERVAL_SECONDS = 0.025;

export interface SchedulerOptions {
  readonly clock: Clock;
  readonly onAction: (action: RuntimeAction) => void;
  readonly timer?: TimerDriver;
  readonly lookaheadSeconds?: number;
  readonly tickIntervalSeconds?: number;
}

interface PendingRelease {
  readonly time: number;
  readonly action: ReleaseAction;
  readonly trackId: string;
}

let voiceCounter = 0;
function nextVoiceId(): string {
  voiceCounter += 1;
  return `voice-${voiceCounter}`;
}

/**
 * Deterministischer, vorausplanender Scheduler fuer eine einzelne Sequenz.
 * Siehe SCHEDULER.md fuer das Timing-Modell und seine Grenzen.
 */
export class Scheduler {
  private readonly clock: Clock;
  private readonly onAction: (action: RuntimeAction) => void;
  private readonly timer: TimerDriver;
  private readonly lookaheadSeconds: number;
  private readonly tickIntervalSeconds: number;

  private doc: SequenceDocument | undefined;
  private eventsByStep: readonly (readonly SequenceEvent[])[] = [];
  private trackById: ReadonlyMap<string, Track> = new Map();

  private playing = false;
  /** Naechster zu planender Step-Index (interner Cursor, kann dem Anzeige-Step voraus sein). */
  private currentStep = 0;
  /** Zuletzt tatsaechlich geplanter Step-Index - das ist, was getCurrentStep() als "aktuell" meldet. */
  private displayStep = 0;
  private nextStepTime = 0;
  private stepDurationSeconds = 0.25;
  private bpm = 120;
  private stepsPerBeat = 4;
  private swing = 0;
  private endReached = false;

  private readonly pendingReleases: PendingRelease[] = [];
  private readonly mutedTracks = new Set<string>();
  private readonly soloedTracks = new Set<string>();

  constructor(options: SchedulerOptions) {
    this.clock = options.clock;
    this.onAction = options.onAction;
    this.timer = options.timer ?? new IntervalTimerDriver();
    this.lookaheadSeconds = options.lookaheadSeconds ?? DEFAULT_LOOKAHEAD_SECONDS;
    this.tickIntervalSeconds = options.tickIntervalSeconds ?? DEFAULT_TICK_INTERVAL_SECONDS;
  }

  /** Laedt eine neue Sequenz. Beendet vorher sauber alle aktiven Stimmen der vorherigen. */
  loadPattern(doc: SequenceDocument): void {
    this.haltAndDrain('transportStop');
    this.doc = doc;
    this.bpm = doc.timing.bpm;
    this.stepsPerBeat = doc.timing.stepsPerBeat;
    this.swing = doc.timing.swing ?? 0;
    this.stepDurationSeconds = this.computeStepDuration();
    this.trackById = new Map(doc.tracks.map((track) => [track.id, track]));
    this.eventsByStep = groupEventsByStep(doc.events, doc.timing.lengthSteps);
    this.currentStep = 0;
    this.displayStep = 0;
    this.endReached = false;
    this.mutedTracks.clear();
    this.soloedTracks.clear();
    for (const track of doc.tracks) {
      if (track.mute) this.mutedTracks.add(track.id);
      if (track.solo) this.soloedTracks.add(track.id);
    }
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  getCurrentStep(): number {
    return this.displayStep;
  }

  /** Startet die Wiedergabe. Wiederholte Aufrufe waehrend des Abspielens erzeugen keinen zweiten Timer. */
  play(): void {
    if (this.playing) {
      return;
    }
    if (!this.doc) {
      throw new Error('Scheduler.play() ohne geladene Sequenz aufgerufen.');
    }
    this.playing = true;
    this.endReached = false;
    this.nextStepTime = this.clock.now();
    this.onAction({ kind: 'transportStart', time: this.clock.now() });
    this.timer.start(() => this.tick(), this.tickIntervalSeconds);
    this.tick();
  }

  /** Stoppt die Wiedergabe, beendet alle aktiven Stimmen sofort und setzt die Position zurueck. */
  stop(): void {
    this.haltAndDrain('transportStop');
  }

  restart(): void {
    this.stop();
    this.play();
  }

  /** Beendet aktive UND geplante Ereignisse sofort und sendet ein panic-Signal an alle Konsumenten. */
  panic(): void {
    this.haltAndDrain('panic');
  }

  setBpm(bpm: number): void {
    this.bpm = bpm;
    this.stepDurationSeconds = this.computeStepDuration();
  }

  setMute(trackId: string, mute: boolean): void {
    if (mute) {
      this.mutedTracks.add(trackId);
      this.releaseTrackNow(trackId);
    } else {
      this.mutedTracks.delete(trackId);
    }
  }

  setSolo(trackId: string, solo: boolean): void {
    if (solo) {
      this.soloedTracks.add(trackId);
    } else {
      this.soloedTracks.delete(trackId);
    }
  }

  private computeStepDuration(): number {
    const secondsPerBeat = 60 / this.bpm;
    return secondsPerBeat / this.stepsPerBeat;
  }

  private isTrackAudible(trackId: string): boolean {
    if (this.soloedTracks.size > 0) {
      return this.soloedTracks.has(trackId);
    }
    return !this.mutedTracks.has(trackId);
  }

  private tick(): void {
    if (!this.playing || !this.doc) {
      return;
    }
    const horizon = this.clock.now() + this.lookaheadSeconds;

    while (!this.endReached && this.nextStepTime < horizon) {
      this.scheduleStep(this.currentStep, this.nextStepTime);
      this.displayStep = this.currentStep;
      const swingOffset = this.currentStep % 2 === 1 ? this.swing * 0.5 * this.stepDurationSeconds : 0;
      this.nextStepTime += this.stepDurationSeconds + swingOffset;
      const isLastStep = this.currentStep >= this.doc.timing.lengthSteps - 1;
      if (isLastStep && !this.doc.timing.loop) {
        this.endReached = true;
      } else {
        this.currentStep = (this.currentStep + 1) % this.doc.timing.lengthSteps;
      }
    }

    this.flushDueReleases(horizon);

    if (this.endReached && this.pendingReleases.length === 0) {
      this.haltAndDrain('transportStop');
    }
  }

  private scheduleStep(step: number, time: number): void {
    const doc = this.doc;
    if (!doc) return;
    const events = this.eventsByStep[step] ?? [];
    for (const event of events) {
      if (!this.isTrackAudible(event.trackId)) {
        continue;
      }
      const track = this.trackById.get(event.trackId);
      if (!track) continue;
      this.scheduleEvent(doc, track, event, step, time);
    }
  }

  private scheduleEvent(doc: SequenceDocument, track: Track, event: SequenceEvent, step: number, time: number): void {
    const sequenceTime = step * this.stepDurationSeconds;
    const voiceId = nextVoiceId();
    const common = {
      time,
      sequenceTime,
      patternId: doc.id,
      trackId: track.id,
      role: track.role,
      step,
      voiceId,
    } as const;

    if (event.kind === 'note' || event.kind === 'chord' || event.kind === 'drum') {
      const gateSeconds = event.kind === 'drum' ? 0 : event.gate * this.stepDurationSeconds;
      const payload = buildPayload(event);
      const trigger: TriggerAction = {
        ...common,
        kind: 'trigger',
        eventKind: event.kind,
        payload,
        gateSeconds,
      };
      this.onAction(trigger);

      if (gateSeconds > 0) {
        const release: ReleaseAction = {
          ...common,
          kind: 'release',
          eventKind: event.kind,
          time: time + gateSeconds,
        };
        this.pendingReleases.push({ time: release.time, action: release, trackId: track.id });
        this.pendingReleases.sort((a, b) => a.time - b.time);
      }
    } else {
      this.onAction({
        ...common,
        kind: 'control',
        controlId: event.controlId,
        value: event.value,
        ...(event.stateRef !== undefined ? { stateRef: event.stateRef } : {}),
        ...(event.sceneRef !== undefined ? { sceneRef: event.sceneRef } : {}),
      });
    }
  }

  private flushDueReleases(horizon: number): void {
    while (this.pendingReleases.length > 0 && (this.pendingReleases[0] as PendingRelease).time < horizon) {
      const due = this.pendingReleases.shift() as PendingRelease;
      this.onAction(due.action);
    }
  }

  private releaseTrackNow(trackId: string): void {
    const now = this.clock.now();
    const remaining: PendingRelease[] = [];
    for (const pending of this.pendingReleases) {
      if (pending.trackId === trackId) {
        this.onAction({ ...pending.action, time: now });
      } else {
        remaining.push(pending);
      }
    }
    this.pendingReleases.length = 0;
    this.pendingReleases.push(...remaining);
  }

  private haltAndDrain(signal: 'transportStop' | 'panic'): void {
    const wasPlaying = this.playing;
    this.timer.stop();
    this.playing = false;
    this.endReached = false;
    this.currentStep = 0;
    this.displayStep = 0;
    const now = this.clock.now();
    for (const pending of this.pendingReleases) {
      this.onAction({ ...pending.action, time: now });
    }
    this.pendingReleases.length = 0;
    if (signal === 'panic') {
      this.onAction({ kind: 'panic', time: now });
    } else if (wasPlaying) {
      this.onAction({ kind: 'transportStop', time: now });
    }
  }
}

function buildPayload(event: SequenceEvent): NotePayload | ChordPayload | DrumPayload {
  if (event.kind === 'note') {
    return {
      ...(event.note !== undefined ? { note: event.note } : {}),
      ...(event.scaleDegree !== undefined ? { scaleDegree: event.scaleDegree } : {}),
      velocity: event.velocity,
    };
  }
  if (event.kind === 'chord') {
    return { degree: event.degree, quality: event.quality, velocity: event.velocity };
  }
  if (event.kind === 'drum') {
    return { triggerId: event.triggerId, velocity: event.velocity };
  }
  throw new Error('buildPayload erwartet ein note/chord/drum-Event.');
}

function groupEventsByStep(events: readonly SequenceEvent[], lengthSteps: number): readonly (readonly SequenceEvent[])[] {
  const groups: SequenceEvent[][] = Array.from({ length: lengthSteps }, () => []);
  for (const event of events) {
    const bucket = groups[event.step];
    if (bucket) {
      bucket.push(event);
    }
  }
  return groups;
}
